import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { GridColDef } from "@mui/x-data-grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { LojaPageContainer, LojaDataGrid } from "layouts/pages/loja/common";
import { useMaterialUIController } from "context";
import { debouce } from "helpers/utils";
import { IModalidade } from "interfaces/eventos";
import AddAndEditarModalidade from "./components/AddAndEditarModalidade";
import RemoverModalidade from "./components/removerModalidade";
import { MODALIDADES_ACCENT, modalidadesToolbarSx } from "./modalidades.theme";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? MODALIDADES_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function Modalidades() {
  const [controller] = useMaterialUIController();
  const { dataConfig } = controller;
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<IModalidade[] | null>(null);
  const [modalidadeSelected, setModalidadeSelected] = useState<IModalidade | null>(
    null,
  );
  const [openModal, setOpenModal] = useState(false);

  const modalidades: IModalidade[] = dataConfig.modalidade ?? [];
  const isFiltered = searchResults !== null;
  const displayedList = searchResults ?? modalidades;

  useEffect(() => {
    setSearchResults(null);
    setSearch("");
  }, [dataConfig.modalidade]);

  const applySearch = useCallback(
    (term: string) => {
      const q = term.trim().toLowerCase();
      if (!q) {
        setSearchResults(null);
        return;
      }
      setSearchResults(
        modalidades.filter((m) => m.tipo.toLowerCase().includes(q)),
      );
    },
    [modalidades],
  );

  const debouncedSearch = useMemo(
    () => debouce(applySearch, 300),
    [applySearch],
  );

  const handleSelectModalidade = (modalidade: IModalidade) => {
    setModalidadeSelected(modalidade);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalidadeSelected(null);
  };

  const handleAddModalidade = () => {
    setModalidadeSelected(null);
    setOpenModal(true);
  };

  const columns: GridColDef<IModalidade>[] = useMemo(
    () => [
      {
        field: "tipo",
        headerName: "Modalidade",
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {params.row.tipo}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 120,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <RemoverModalidade
              listModalidade={modalidades}
              itemDeleted={params.row}
            />
            <Tooltip title="Editar modalidade">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectModalidade(params.row);
                }}
              >
                <EditOutlinedIcon fontSize="small" sx={{ color: MODALIDADES_ACCENT }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [modalidades],
  );

  return (
    <LojaPageContainer
      title="Modalidades"
      subtitle="Tipos de modalidade usados nos eventos esportivos."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={handleAddModalidade}
          sx={{
            background: `linear-gradient(195deg, ${MODALIDADES_ACCENT}, #e04a1a)`,
          }}
        >
          Nova modalidade
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4}>
          <StatMini
            label={isFiltered ? "Resultados da busca" : "Total cadastradas"}
            value={isFiltered ? displayedList.length : modalidades.length}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatMini label="Exibindo agora" value={displayedList.length} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatMini
            label="Cadastro global"
            value={modalidades.length === 1 ? "1 tipo" : `${modalidades.length} tipos`}
          />
        </Grid>
      </Grid>

      <MDBox sx={modalidadesToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CategoryOutlinedIcon sx={{ color: MODALIDADES_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isFiltered
                ? `${displayedList.length} modalidade${displayedList.length === 1 ? "" : "s"} no filtro`
                : `${modalidades.length} modalidade${modalidades.length === 1 ? "" : "s"} cadastrada${modalidades.length === 1 ? "" : "s"}`}
            </MDTypography>
          </Stack>

          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel htmlFor="modalidades-search">Pesquisar modalidade</InputLabel>
            <OutlinedInput
              id="modalidades-search"
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debouncedSearch(e.target.value);
              }}
              label="Pesquisar modalidade"
              placeholder="Nome da modalidade…"
              endAdornment={
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>
        </Stack>
      </MDBox>

      {displayedList.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <CategoryOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhuma modalidade encontrada
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isFiltered
              ? "Ajuste a busca ou limpe o campo para ver todas."
              : "Cadastre a primeira modalidade para usar nos eventos."}
          </MDTypography>
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={displayedList}
          columns={columns}
          getRowId={(row) => row.id ?? row.tipo}
          onRowClick={(params) => handleSelectModalidade(params.row)}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(249, 96, 48, 0.04)",
            },
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "grey.100",
              fontWeight: 700,
            },
          }}
        />
      )}

      <AddAndEditarModalidade
        open={openModal}
        onClose={handleCloseModal}
        modalidade={modalidadeSelected}
      />
    </LojaPageContainer>
  );
}
